import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type { AppDispatch } from "_shared/_stores/store-types";
import { useEffect, useState } from "react";
import type { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import { userNetworkConnectionsApi } from "users/networking/connections/_api/user-network-connection-api";

const PAGE_SIZE = 100;

/**
 * Every one of the caller's network connections, read one page at a time.
 *
 * Interim, for the client selector only (#5434, plan Task 9): it searches its roster in the browser
 * until the organisation members endpoint pages (#5442), so it still needs the whole list. Do not
 * use this anywhere else. Page and search on the server with `useInfiniteNetworkConnections`.
 */
export default function useEveryNetworkConnection(): { data?: UserInformationJSON[]; isLoading: boolean } {
  const dispatch = useAttorneyHubDispatch();
  const [connections, setConnections] = useState<UserInformationJSON[] | undefined>(undefined);

  useEffect(() => {
    let isCancelled = false;
    loadEveryNetworkConnection(dispatch, () => isCancelled).then(
      (loaded) => {
        if (!isCancelled) setConnections(loaded);
      },
      () => {
        if (!isCancelled) setConnections([]);
      },
    );
    return () => {
      isCancelled = true;
    };
  }, [dispatch]);

  return { data: connections, isLoading: connections === undefined };
}

async function loadEveryNetworkConnection(
  dispatch: AppDispatch,
  isCancelled: () => boolean,
): Promise<UserInformationJSON[]> {
  const connections: UserInformationJSON[] = [];
  for (let pageIndex = 0; ; pageIndex += 1) {
    const request = dispatch(
      userNetworkConnectionsApi.endpoints.getUserNetworkConnections.initiate({
        query: new NetworkConnectionQuery({
          paginationParameters: PaginationParameters.create({ pageIndex, pageSize: PAGE_SIZE }),
        }),
      }),
    );
    const page = await request.unwrap().finally(() => request.unsubscribe());
    connections.push(...page.data);
    if (isCancelled() || !hasNextPage(page)) return connections;
  }
}

function hasNextPage(page: Paginated<UserInformationJSON>): boolean {
  return page.headers.hasNext ?? page.headers.pageIndex + 1 < page.headers.totalPages;
}
